import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { SystemStatusContext } from '../shared/contexts/system-status.context';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { CurrentDateContext } from '../shared/contexts/current-date.context';
import { LoginRequest, Role, State, StatusCategory, SystemHealthStatus } from '../shared/models/generated/ziroModels';
import { AuthenticationContext } from '../shared/contexts/authentication.context';
import { ZiroIcons } from '../shared/models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles } from '../button/button.component';
import { ZiroFormAbstractDirective } from '../forms/ziro-form-abstract.directive';
import { HtmlInputType, ZiroTextConfig } from '../forms/fields/text/ziro-text.component';
import { ZiroFormConfig } from '../forms/ziro-forms-models';
import { SiteSummaryContext } from '../shared/contexts/site-summary.context';
import { catchError, finalize, map } from 'rxjs/operators';
import { ZiroFormStateService } from '../forms/ziro-form-state.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { GlobalPropertiesContext } from '../shared/contexts/global-properties.context';
import { CustomUiOptionsContext } from '../shared/contexts/custom-ui-options.context';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

interface LoginErrorAlert {
  header: string;
  message: string | { content: string; params: { [key: string]: string } };
}

interface LoginInfoAlert {
  branch: string;
  environment: string;
}

enum LoginURLs {
  ZPC_DEV = 'zpc-develop.labs.goziro.com',
  ZPC_RC = 'zpc-rc.labs.goziro.com',
  ZPC_MASTER = 'zpc-master.labs.goziro.com',
  ZPM_DEV = 'zpm-develop.app.goziro.com',
  ZPM_RC = 'zpm-rc.app.goziro.com',
  ZPM_MASTER = 'zpm-master.app.goziro.com',
}

enum SsoAuthFailureTypes {
  MICROSOFT_NO_SERVICE_ACCOUNTS_CONFIGURED = 'MICROSOFT_NO_SERVICE_ACCOUNTS_CONFIGURED',
  MICROSOFT_USER_HAS_NO_PERMISSIONS = 'MICROSOFT_USER_HAS_NO_PERMISSIONS',
  UNHANDLED_FAILURE = 'UNHANDLED_FAILURE',
}

const inactiveSideError = 'This side is inactive';

@Component({
  selector: 'ziro-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent extends ZiroFormAbstractDirective<LoginRequest> implements OnInit, OnDestroy {
  private systemStatusContext = inject(SystemStatusContext);
  private router = inject(Router);
  private currentDateContext = inject(CurrentDateContext);
  private authenticationContext = inject(AuthenticationContext);
  private siteSummaryContext = inject(SiteSummaryContext);
  private domSanitizer = inject(DomSanitizer);
  private _activatedRoute = inject(ActivatedRoute);
  private globalPropertiesContext = inject(GlobalPropertiesContext);
  private customUiOptionsContext = inject(CustomUiOptionsContext);
  private _translateService = inject(TranslateService);
  private _userHasBypassedSso = false;
  protected _ziroFormStateService: ZiroFormStateService;

  // Translation Maps - initialized with tkey strings, updated with translations in ngOnInit
  ERROR_HEADERS_MAP: Map<string, string> = new Map([
    ['global.login.fail', 'tkey;global.login.fail.header'],
    ['pages.login.site.denied', 'tkey;pages.login.site.denied.header'],
  ]);

  ERROR_MESSAGES_MAP: Map<string, string> = new Map([
    ['global.login.fail', 'tkey;global.login.fail.message'],
    ['pages.login.incomplete_config', 'tkey;pages.login.incomplete_config.text'],
    ['pages.login.site.denied', 'tkey;pages.login.site.denied.message'],
    ['pages.login.no_phone_services', 'tkey;pages.login.no_phone_services.text'],
    ['pages.login.site.disabled_user', 'tkey;pages.login.site.disabled_user.message'],
  ]);

  SYSTEM_STATUS_MAP: Map<string, string> = new Map([
    ['error.header', 'tkey;pages.login.error.header'],
    ['error.message', 'tkey;pages.login.error.message'],
    ['warning.header', 'tkey;pages.login.warning.header'],
    ['warning.message', 'tkey;pages.login.warning.message'],
  ]);

  SSO_ERROR_MAP: Map<string, string> = new Map([
    ['ms_no_service_accounts.header', 'tkey;pages.login.error.title.ms_no_service_accounts'],
    ['ms_no_service_accounts.message', 'tkey;pages.login.error.message.ms_no_service_accounts'],
    ['ms_no_permissions.header', 'tkey;pages.login.error.title.ms_no_permissions'],
    ['ms_no_permissions.message', 'tkey;pages.login.error.message.ms_no_permissions'],
    ['ms_no_permissions.message.ziro_support', 'tkey;pages.login.error.message.ms_no_permissions.ziro_support'],
    ['unhandled_failure.header', 'tkey;pages.login.error.title.unhandled_failure'],
    ['unhandled_failure.message', 'tkey;pages.login.error.message.unhandled_failure'],
  ]);

  INFO_ALERT_MAP: Map<string, string> = new Map([
    ['branch.develop', 'tkey;pages.login.info_message.branch_develop.text'],
    ['branch.rc', 'tkey;pages.login.info_message.branch_rc.text'],
    ['branch.master', 'tkey;pages.login.info_message.branch_master.text'],
    ['environment.cisco', 'tkey;pages.login.info_message.environment_cisco.text'],
    ['environment.microsoft', 'tkey;pages.login.info_message.environment_microsoft.text'],
  ]);

  OTHER_STRINGS_MAP: Map<string, string> = new Map([
    ['bypass.sso', 'tkey;pages.login.bypass.sso'],
    ['pages.login.submit', 'tkey;pages.login.submit'],
    ['pages.login.microsoft_oidc', 'tkey;pages.login.microsoft_oidc'],
    ['global.login.browser_outdated.header', 'tkey;global.login.browser_outdated.header'],
    ['global.login.browser_outdated.message', 'tkey;global.login.browser_outdated.message'],
    ['pages.login.username', 'tkey;pages.login.username.placeholder'],
    ['pages.login.password', 'tkey;pages.login.password.placeholder'],
  ]);

  // Error messages array is rebuilt after translations are loaded
  errorMessages: Array<{ serverError: string; errorMessage: { header: string; message: string } }> = [];

  systemStatusErrorMessage: LoginErrorAlert = {
    header: this.SYSTEM_STATUS_MAP.get('error.header'),
    message: this.SYSTEM_STATUS_MAP.get('error.message'),
  };

  systemStatusWarningMessage: LoginErrorAlert = {
    header: this.SYSTEM_STATUS_MAP.get('warning.header'),
    message: this.SYSTEM_STATUS_MAP.get('warning.message'),
  };

  window = window;
  isLoading = true;
  year: number;
  loginLogo: string | SafeResourceUrl;
  loginStyles: string;
  isSamlSsoEnabled: boolean;
  isSsoBypassed = false;
  bypassSsoLabel: { content: string; params: { [key: string]: string } };
  systemHealthStatus: SystemHealthStatus;
  attemptedLogin = false;
  errorAlert: LoginErrorAlert;
  infoAlert: LoginInfoAlert;
  infoAlertLogo: string;
  outdatedBrowserError = false;
  isLoggingIn = false;
  ziroIcons = ZiroIcons;
  buttonSizes = ButtonSizes;
  buttonStyles = ButtonStyles;
  loginSubmitLabel: string = this.OTHER_STRINGS_MAP.get('pages.login.submit');
  microsoftOidcLabel: string = this.OTHER_STRINGS_MAP.get('pages.login.microsoft_oidc');
  browserOutdatedHeader: string = this.OTHER_STRINGS_MAP.get('global.login.browser_outdated.header');
  browserOutdatedMessage: string = this.OTHER_STRINGS_MAP.get('global.login.browser_outdated.message');
  formConfig = {
    fields: {
      username: {
        label: () => this.OTHER_STRINGS_MAP.get('pages.login.username'),
        placeholder: '',
        dataAutomation: 'login-username',
        required: true,
      },
      password: {
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.PASSWORD }),
        label: () => this.OTHER_STRINGS_MAP.get('pages.login.password'),
        placeholder: '',
        dataAutomation: 'login-password',
        required: true,
      },
    },
  } as ZiroFormConfig;

  private _isHostedEnabled: boolean;
  private _subs = new Subscription();
  private defaultBackgroundColor = '#202020';

  private static _hasFlatMapSupport(): boolean {
    try {
      [].flatMap(() => {});
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (err) {
      return false;
    }
    return true;
  }

  ngOnInit(): void {
    const translationKeys = [
      ...Array.from(this.ERROR_HEADERS_MAP.values()),
      ...Array.from(this.ERROR_MESSAGES_MAP.values()),
      ...Array.from(this.SYSTEM_STATUS_MAP.values()),
      ...Array.from(this.SSO_ERROR_MAP.values()),
      ...Array.from(this.INFO_ALERT_MAP.values()),
      ...Array.from(this.OTHER_STRINGS_MAP.values()),
    ];

    const translations$ = this._translateService.get(translationKeys);

    const globalProperties$ = this.globalPropertiesContext.state$.pipe(
      map((globalProperties) => {
        this._isHostedEnabled = globalProperties.hostedEnabled;
        this.infoAlertLogo = this._isHostedEnabled ? this.ziroIcons.MICROSOFT_LOGO : this.ziroIcons.CISCO_LOGO;
      }),
    );

    const customUiOptions$ = this.customUiOptionsContext.state$.pipe(
      map((uiOptions) => {
        this.loginLogo = uiOptions.customLogo
          ? this.domSanitizer.bypassSecurityTrustResourceUrl(`data:image/png;base64,${uiOptions.customLogo}`)
          : '/static/img/ziro-login-logo-sm.png';
        this.loginStyles = `background-color: ${uiOptions.customBackgroundColor || this.defaultBackgroundColor};`;
        this.loginStyles += uiOptions.customBackground
          ? `background-image: url("data:image/png;base64,${uiOptions.customBackground}");`
          : '';
        this.isSamlSsoEnabled = uiOptions.ssoEnabled;
      }),
    );

    const systemStatus$ = this.systemStatusContext.state$.pipe(
      map((systemStatus) => {
        this.systemHealthStatus = systemStatus;
        // This is handled initially in _initializeAfterTranslations but is necessary here to update based on system status
        if (!this.isLoading) {
          this._setErrorMessage();
        }
      }),
    );

    const currentDate$ = this.currentDateContext.state$.pipe(
      map((currentDate) => {
        this.year = currentDate.year;
      }),
    );

    const subscription = combineLatest([
      customUiOptions$,
      systemStatus$,
      currentDate$,
      globalProperties$,
      translations$,
    ]).subscribe((results) => {
      const translations = results[4];
      this._initializeAfterTranslations(translations);
      this.isSamlSsoEnabled = this.isSamlSsoEnabled && !this._isDarkSide();
      if (!this._userHasBypassedSso) {
        this.isSsoBypassed = !this._isHostedEnabled && !this.isSamlSsoEnabled;
      }

      this.isLoading = false;
    });
    this._subs.add(subscription);

    this.outdatedBrowserError = !LoginComponent._hasFlatMapSupport();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subs.unsubscribe();
  }

  onEnterSubmit(): void {
    this._validateAndSubmitSource.next(true);
  }

  showSamlSsoLogin(): boolean {
    return !this.isSsoBypassed && this.isSamlSsoEnabled;
  }

  showOidcSsoLogin(): boolean {
    return this._isHostedEnabled && !this.showSamlSsoLogin();
  }

  systemStatusError(): boolean {
    return this.systemHealthStatus?.globalState === State.ERROR;
  }

  showZiroLogin(): void {
    this._userHasBypassedSso = true;
    this.isSsoBypassed = true;
  }

  handleSamlSsoLogin(): void {
    window.location.href = '/saml2/authenticate/relying-party';
  }

  handleOidcLogin(): void {
    window.location.href = '/oauth2/authorization/entra-id';
  }

  getLoginInfoMessage(): LoginInfoAlert {
    const baseUrl = window.location.hostname;

    switch (baseUrl.toLowerCase()) {
      case LoginURLs.ZPC_DEV: {
        return {
          branch: this.INFO_ALERT_MAP.get('branch.develop'),
          environment: this.INFO_ALERT_MAP.get('environment.cisco'),
        };
      }
      case LoginURLs.ZPC_RC: {
        return {
          branch: this.INFO_ALERT_MAP.get('branch.rc'),
          environment: this.INFO_ALERT_MAP.get('environment.cisco'),
        };
      }
      case LoginURLs.ZPC_MASTER: {
        return {
          branch: this.INFO_ALERT_MAP.get('branch.master'),
          environment: this.INFO_ALERT_MAP.get('environment.cisco'),
        };
      }
      case LoginURLs.ZPM_DEV: {
        return {
          branch: this.INFO_ALERT_MAP.get('branch.develop'),
          environment: this.INFO_ALERT_MAP.get('environment.microsoft'),
        };
      }
      case LoginURLs.ZPM_RC: {
        return {
          branch: this.INFO_ALERT_MAP.get('branch.rc'),
          environment: this.INFO_ALERT_MAP.get('environment.microsoft'),
        };
      }
      case LoginURLs.ZPM_MASTER: {
        return {
          branch: this.INFO_ALERT_MAP.get('branch.master'),
          environment: this.INFO_ALERT_MAP.get('environment.microsoft'),
        };
      }
    }
  }

  /**
   * When SSO auth fails we will be redirected to the login page with a query param. Display error message for param
   */
  getSsoErrorMessage(): LoginErrorAlert {
    const errorQueryParam = decodeURI(this._activatedRoute.snapshot.queryParamMap.get('error')) as SsoAuthFailureTypes;
    const userIdentifierQueryParam = this._activatedRoute.snapshot.queryParamMap.get('userIdentifier');

    switch (errorQueryParam) {
      case SsoAuthFailureTypes.MICROSOFT_NO_SERVICE_ACCOUNTS_CONFIGURED: {
        return {
          header: this.SSO_ERROR_MAP.get('ms_no_service_accounts.header'),
          message: this.SSO_ERROR_MAP.get('ms_no_service_accounts.message'),
        };
      }
      case SsoAuthFailureTypes.MICROSOFT_USER_HAS_NO_PERMISSIONS: {
        return {
          header: this.SSO_ERROR_MAP.get('ms_no_permissions.header'),
          message: {
            content: this._hasZiroDomain()
              ? this.SSO_ERROR_MAP.get('ms_no_permissions.message.ziro_support')
              : this.SSO_ERROR_MAP.get('ms_no_permissions.message'),
            params: {
              user: userIdentifierQueryParam,
              platform: this._isHostedEnabled ? 'ZPM' : 'ZPC',
            },
          },
        };
      }
      case SsoAuthFailureTypes.UNHANDLED_FAILURE: {
        return {
          header: this.SSO_ERROR_MAP.get('unhandled_failure.header'),
          message: this.SSO_ERROR_MAP.get('unhandled_failure.message'),
        };
      }
      default: {
        return null;
      }
    }
  }

  protected submit(): Observable<void> {
    return this._handleLogin();
  }

  private _initializeAfterTranslations(translations: Record<string, string>): void {
    this._updateMapWithTranslations(this.ERROR_HEADERS_MAP, translations);
    this._updateMapWithTranslations(this.ERROR_MESSAGES_MAP, translations);
    this._updateMapWithTranslations(this.SYSTEM_STATUS_MAP, translations);
    this._updateMapWithTranslations(this.SSO_ERROR_MAP, translations);
    this._updateMapWithTranslations(this.INFO_ALERT_MAP, translations);
    this._updateMapWithTranslations(this.OTHER_STRINGS_MAP, translations);

    this.loginSubmitLabel = this.OTHER_STRINGS_MAP.get('pages.login.submit');
    this.microsoftOidcLabel = this.OTHER_STRINGS_MAP.get('pages.login.microsoft_oidc');
    this.browserOutdatedHeader = this.OTHER_STRINGS_MAP.get('global.login.browser_outdated.header');
    this.browserOutdatedMessage = this.OTHER_STRINGS_MAP.get('global.login.browser_outdated.message');
    this.systemStatusErrorMessage = {
      header: this.SYSTEM_STATUS_MAP.get('error.header'),
      message: this.SYSTEM_STATUS_MAP.get('error.message'),
    };
    this.systemStatusWarningMessage = {
      header: this.SYSTEM_STATUS_MAP.get('warning.header'),
      message: this.SYSTEM_STATUS_MAP.get('warning.message'),
    };
    this._rebuildErrorMessages();
    this.infoAlert = this.getLoginInfoMessage();
    this.errorAlert = this.getSsoErrorMessage();
    this._setErrorMessage();
    this._updateBypassSsoLabel();
  }

  private _hasZiroDomain(): boolean {
    const username = this._activatedRoute.snapshot.queryParamMap.get('userIdentifier');
    return username.endsWith('@goziro.com');
  }

  private _handleLogin(): Observable<void> {
    this.isLoggingIn = true;
    this.attemptedLogin = false;
    this.errorAlert = null;

    return this.authenticationContext
      .login({
        username: this.formData.username,
        password: this.formData.password,
      })
      .pipe(
        map((response: string) => {
          this._handleLoginSuccess(response);
        }),
        catchError((response) => {
          return this._handleLoginError(response).pipe(
            finalize(() => {
              this.isLoggingIn = false;
              this._validateAndSubmitSource.next(false);
            }),
          );
        }),
      );
  }

  private _handleLoginSuccess(resp: string): void {
    this._ziroFormStateService.setIsFormDirty(false);
    const systemStatusErrorResp = 'System status error detected';
    const notConfiguredErrorResp = 'No sites or MS Graph have been configured';
    const adminRestrictedResponses = [systemStatusErrorResp, notConfiguredErrorResp];

    if (adminRestrictedResponses.includes(resp)) {
      console.info(`Status ${resp}, redirecting to admin page`);
      if (resp === systemStatusErrorResp) {
        this.router.navigateByUrl(`admin/system-health-status`);
      } else if (resp === notConfiguredErrorResp) {
        if (this._isHostedEnabled) {
          this.router.navigateByUrl(`admin/microsoft/tenant-management`);
        } else {
          window.location.href = window.location.origin + '/app2/#/admin/site-management';
        }
      }
    } else {
      if (this._isHostedEnabled) {
        this._redirectUserHome();
      } else {
        this.siteSummaryContext.refreshSiteSummary().subscribe(() => {
          this._redirectUserHome();
        });
      }
    }
  }

  private _redirectUserHome(): void {
    const currentUser = this.authenticationContext.getCurrentUser();

    if (this.authenticationContext.userIsAtLeast(currentUser, Role.S8_SITE_BASED_HELPDESK)) {
      // Redirect to Ziro home
      if (this.systemHealthStatus.globalState === State.ERROR) {
        this.router.navigateByUrl(`system-health-status`);
      } else {
        this.router.navigateByUrl(`/`);
      }
    } else if (currentUser.role === Role.S8_SELF_SERVE_USER) {
      this.router.navigateByUrl(`self-serve/user/${this.formData.username}`);
    } else {
      window.location.reload();
    }
  }

  private _handleLoginError(httpErrorResponse: HttpErrorResponse): Observable<void> {
    const errorMessage = this.errorMessages.find(
      (message) => message.serverError === httpErrorResponse.error.description,
    );
    this.attemptedLogin = true;
    if (errorMessage) {
      this.errorAlert = errorMessage.errorMessage;
    } else if (this.systemStatusError()) {
      this._setErrorMessage();
    } else {
      // if there was no health status error before, check again to see if one occurred since the last time we polled.
      return this.systemStatusContext.getSystemStatus().pipe(
        map((healthStatus) => {
          if (healthStatus.globalState !== State.ERROR) {
            // if there is still no health status error, then this is some unhandled error.
            throw httpErrorResponse;
          }
        }),
      );
    }
    return of(null);
  }

  private _setErrorMessage(): void {
    if (this.getSsoErrorMessage()) {
      return;
    }

    if (!this.systemHealthStatus) {
      return;
    }

    const errorHeader = this.SYSTEM_STATUS_MAP.get('error.header');
    const errorMessage = this.SYSTEM_STATUS_MAP.get('error.message');
    const warningHeader = this.SYSTEM_STATUS_MAP.get('warning.header');
    const warningMessage = this.SYSTEM_STATUS_MAP.get('warning.message');

    if (this.systemHealthStatus.globalState === State.ERROR) {
      this.errorAlert = { header: errorHeader, message: errorMessage } as LoginErrorAlert;
    } else if (this.systemHealthStatus.globalState === State.WARNING) {
      this.errorAlert = { header: warningHeader, message: warningMessage } as LoginErrorAlert;
    } else if (this._isSystemStatusAlert(this.errorAlert)) {
      // reset to clean state
      this.errorAlert = null;
    }
  }

  private _isSystemStatusAlert(alert: LoginErrorAlert): boolean {
    if (!alert) {
      return false;
    }
    const headers = [this.SYSTEM_STATUS_MAP.get('error.header'), this.SYSTEM_STATUS_MAP.get('warning.header')];
    return headers.includes(alert.header);
  }

  private _isDarkSide(): boolean {
    const trafficStatusState = this.systemHealthStatus?.healthStatuses.find(
      (status) => status.category === StatusCategory.TRAFFIC,
    )?.cause;
    if (trafficStatusState) {
      return trafficStatusState === inactiveSideError;
    } else {
      return false;
    }
  }

  private _updateMapWithTranslations(map: Map<string, string>, translations: Record<string, string>): void {
    map.forEach((value, key) => {
      // Only translate if the value is still a tkey (hasn't been translated yet)
      if (value && value.startsWith('tkey')) {
        map.set(key, translations[value] ?? value);
      }
    });
  }

  private _updateBypassSsoLabel(): void {
    const ssoType = this.showSamlSsoLogin() ? 'SAML' : 'OIDC';
    const content = this._translateService.instant('tkey;pages.login.bypass.sso', { ssoType });
    this.bypassSsoLabel = { content, params: { ssoType } };
  }

  private _rebuildErrorMessages(): void {
    this.errorMessages = [
      {
        serverError: 'Authentication failed',
        errorMessage: {
          header: this.ERROR_HEADERS_MAP.get('global.login.fail'),
          message: this.ERROR_MESSAGES_MAP.get('global.login.fail'),
        },
      },
      {
        serverError: 'No sites or MS Graph have been configured',
        errorMessage: {
          header: this.ERROR_HEADERS_MAP.get('pages.login.site.denied'),
          message: this.ERROR_MESSAGES_MAP.get('pages.login.incomplete_config'),
        },
      },
      {
        serverError: 'Site Based Helpdesk user does not have access to any sites',
        errorMessage: {
          header: this.ERROR_HEADERS_MAP.get('pages.login.site.denied'),
          message: this.ERROR_MESSAGES_MAP.get('pages.login.site.denied'),
        },
      },
      {
        serverError: 'Self-serve user has no phone services',
        errorMessage: {
          header: this.ERROR_HEADERS_MAP.get('pages.login.site.denied'),
          message: this.ERROR_MESSAGES_MAP.get('pages.login.no_phone_services'),
        },
      },
      {
        serverError: 'Site Based Helpdesk user is disabled',
        errorMessage: {
          header: this.ERROR_HEADERS_MAP.get('pages.login.site.denied'),
          message: this.ERROR_MESSAGES_MAP.get('pages.login.site.disabled_user'),
        },
      },
    ];
  }
}
